import { FormEvent, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface AddSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { name: string; url: string; expectedContent?: string }) => Promise<void>;
}

export function AddSiteDialog({ open, onOpenChange, onSubmit }: AddSiteDialogProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [expectedContent, setExpectedContent] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (!url.trim()) {
      setError('URL é obrigatória');
      return;
    }

    // URL must be https://
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      setError('URL deve começar com http:// ou https://');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({
        name: name.trim(),
        url: url.trim(),
        expectedContent: expectedContent.trim() || undefined
      });

      // Reset form
      setName('');
      setUrl('');
      setExpectedContent('');
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset on close
      setName('');
      setUrl('');
      setExpectedContent('');
      setError('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar Site</DialogTitle>
          <DialogDescription>
            Configure um novo site para ser monitorado automaticamente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded p-3">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Nome do Site</Label>
            <Input
              id="name"
              placeholder="Ex: Site Principal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">URL</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://exemplo.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Deve começar com http:// ou https://
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expectedContent">Conteúdo Esperado (opcional)</Label>
            <Input
              id="expectedContent"
              placeholder="Ex: Bem-vindo"
              value={expectedContent}
              onChange={(e) => setExpectedContent(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Texto que deve aparecer na página para considerar o site funcionando corretamente
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Criando...' : 'Criar Site'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
